import React from 'react';
import {LineChart} from '@mui/x-charts';
import {ThrowPackingHistoricalData, Prediction} from '../../../../autogenerate/api.generated.clients';
import ThrowPackingTempLineChartTooltip from "./PackingTempLineChartTooltip/ThrowPackingTempLineChartTooltip";
import './throwPackingTempsLineChart.scss';
import {Skeleton} from "@mui/material";
import {axisClasses} from "@mui/x-charts/ChartsAxis";

export interface PackingTempsLineChartProps {
    data: ThrowPackingHistoricalData | undefined;
    isLoading: boolean;
}

interface PackingTempsLineChartPoint {
    x: Date,
    y: number | null,
    prediction: Prediction
}

interface PackingTempsLineChartGroupedSeries {
    color: string,
    predictionString: string,
    data: PackingTempsLineChartPoint[]
}

const ThrowPackingTempsLineChart: React.FC<PackingTempsLineChartProps> = ({data, isLoading}) => {
    if (isLoading || !data) {
        return (<div className={'packing-temps-line-chart-container'} style={{padding: '2em'}}>
            <Skeleton variant="rectangular" height={'100%'} width={'100%'}/>
        </div>);
    }

    const seriesForPredictions = getSeriesForPredictions(data);

    return (
        <div className={'packing-temps-line-chart-container'}>

            <LineChart
                grid={{vertical: true, horizontal: true}}
                xAxis={[{
                    label: 'Timestamp',
                    data: seriesForPredictions[0]?.data.map(point => point.x),
                    scaleType: 'time',
                }]}
                yAxis={[{
                    label: "Temperature ºF",
                    sx: {margin: '1em'}
                }]}
                series={seriesForPredictions.map(series => ({
                    data: series.data.map(point => point.y),
                    area: true,
                    color: series.color,
                    id: series.predictionString,
                    disableHighlight: true,
                }))}
                tooltip={{trigger: 'item'}}
                slots={{
                    itemContent: ThrowPackingTempLineChartTooltip
                }}
                sx={{
                    paddingLeft: '1em',
                    [`.${axisClasses.left} .${axisClasses.label}`]: {
                        transform: 'translate(-10px, 0)',
                    },
                    [`.${axisClasses.right} .${axisClasses.label}`]: {
                        transform: 'translate(13px, 0)',
                    },
                }}
            >
            </LineChart>
        </div>
    );
};


function getSeriesForPredictions(data: ThrowPackingHistoricalData) {
    const chartPoints = data.packingVentTemperatures.map((point, index) => ({
        x: new Date(point.x),
        y: point.y,
        prediction: point.prediction
    } as PackingTempsLineChartPoint));
    chartPoints.sort((a, b) => a.x.getTime() - b.x.getTime());

    const uniquePredictions = Array.from(new Set(data.packingVentTemperatures.map(point => point.prediction)));
    let series: PackingTempsLineChartGroupedSeries[] = uniquePredictions.map(prediction => ({
        predictionString: Prediction[prediction],
        color: getColor(prediction),
        data: []
    }));

    chartPoints.forEach((point, index) => {
        const color = getColor(point.prediction);
        let group = series.find(g => g.color === color);
        group!.data.push({x: point.x, y: point.y, prediction: point.prediction});


        series.forEach(g => {
            if (g.color !== color) {
                if (index - 1 > 0 && chartPoints[index - 1]) {
                    const previousColor = getColor(chartPoints[index - 1].prediction);
                    if (previousColor === g.color) {
                        g.data.push({x: point.x, y: point.y, prediction: point.prediction});
                    } else {
                        g.data.push({x: point.x, y: null, prediction: point.prediction});
                    }
                } else {
                    g.data.push({x: point.x, y: null, prediction: point.prediction});
                }
            }
        });
    });
    return series;
}

function getColor(prediction: Prediction) {
    switch (prediction) {
        case Prediction.NormalOperation:
            return '#497b37';
        case Prediction.LeakDetected:
            return '#af3219';
        case Prediction.AbnormalTemperature:
            return '#f9ae33';
        default:
            return '#9e9e9e';
    }
}

export default ThrowPackingTempsLineChart;